import React from 'react';
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Rocket, ExternalLink } from 'lucide-react';

export function AddTokenLogo() {
  return (
    <article className="prose prose-gray dark:prose-invert max-w-none">
      <header className="space-y-4">
        <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent" style={{
          backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
        }}>
          How to Add Token Logo
        </h1>
        
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-12 h-12 rounded-full overflow-hidden p-[2px]" style={{
            backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
          }}>
            <div className="w-full h-full rounded-full overflow-hidden">
              <img 
                src="/authors/dtdevmdbappio.jpg"
                alt="D. Toutant"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=DT&backgroundColor=${encodeURIComponent(getComputedStyle(document.documentElement).getPropertyValue('--color-header-accent') || '0A74FF').replace('#', '')}`;
                }}
              />
            </div>
          </div>
          <div>
            <p className="font-medium">D. Toutant (MDB Dev)</p>
            <p className="text-sm">Published on December 27, 2023</p>
          </div>
        </div>
      </header>

      <p className="lead">
        Ready to make your Ethereum or BSC token stand out from the crowd? Let's give it a face that your community
        will recognize and love! In this guide, we'll walk you through the process of adding your logo
        to your token on Etherscan or BSCScan, making it visible across various platforms like Trust Wallet,
        MetaMask, and more.
      </p>

      <div className="not-prose mb-8">
        <Card className="border-2" style={{
          background: `linear-gradient(to right, var(--color-header-accent, #0A74FF)15, var(--color-secondary, #10b981)30, var(--color-header-accent, #0A74FF)15)`,
          borderColor: `var(--color-header-accent, #0A74FF)`
        }}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Rocket className="w-6 h-6 flex-shrink-0 mt-1" style={{
                color: `var(--color-header-accent, #0A74FF)`
              }} />
              <div>
                <h3 className="font-semibold mb-2" style={{
                  color: `var(--color-text, #e5e5e5)`
                }}>
                  Haven't Created Your Token Yet?
                </h3>
                <p className="mb-4" style={{
                  color: `var(--color-text, #e5e5e5)`,
                  opacity: 0.8
                }}>
                  Before adding a logo, you'll need a token! Create your custom token on Ethereum or BSC in minutes with
                  our user-friendly token creator.
                </p>
                <Button
                  className="text-white hover:opacity-90"
                  style={{
                    background: `var(--color-header-accent, #0A74FF)`
                  }}
                  onClick={() => window.open('https://mdbapp.io/bscchain/create', '_blank')}
                >
                  Create Your Token Now
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <h2>Step 1: Prepare Your Perfect Logo 🖼️</h2>
      <p>
        First things first - let's get your logo looking just right! Your token's logo is its digital
        face in the crypto world, so let's make it shine:
      </p>
      <ul>
        <li>Format: PNG format (transparent background recommended)</li>
        <li>Size: 32x32 pixels (perfect for wallet displays)</li>
        <li>File size: Keep it under 100KB</li>
        <li>Quality: Clear and recognizable even at small sizes</li>
      </ul>
      <div className="p-4 rounded-lg my-6" style={{
        backgroundColor: `var(--color-header-accent, #0A74FF)20`,
        border: `1px solid var(--color-header-accent, #0A74FF)50`
      }}>
        <p className="text-sm" style={{
          color: `var(--color-text, #e5e5e5)`
        }}>
          <strong>Pro Tip:</strong> Create a simple, memorable design that looks good both in light and
          dark modes. Your logo will be displayed in various sizes across different platforms!
        </p>
      </div>

      <h2>Step 2: Navigate to the Block Explorer 🔍</h2>
      <p>
        Time to get your logo out there! Head over to the appropriate block explorer where your token lives:
      </p>
      <ol>
        <li>Visit <a href="https://etherscan.io" target="_blank" rel="noopener noreferrer">Etherscan</a> for Ethereum tokens or <a href="https://bscscan.com" target="_blank" rel="noopener noreferrer">BSCScan</a> for BSC tokens</li>
        <li>Connect your wallet (make sure it's the one that created or owns the token)</li>
        <li>Search for your token using its contract address</li>
      </ol>

      <h2>Step 3: Update Your Token's Profile 📝</h2>
      <p>
        Found your token? Great! Now let's give it some personality:
      </p>
      <ol>
        <li>Look for the "Update Token Info" button (usually near the token details)</li>
        <li>Click on "Social Profiles" or "Token Information"</li>
        <li>You'll need to fill out some basic information:
          <ul>
            <li>Official website (if you have one)</li>
            <li>Social media links (optional but recommended)</li>
            <li>Project description (make it count!)</li>
            <li>Logo URL (this is where your prepared image comes in)</li>
          </ul>
        </li>
      </ol>

      <div className="p-4 rounded-lg my-6" style={{
        backgroundColor: `var(--color-secondary, #10b981)20`,
        border: `1px solid var(--color-secondary, #10b981)50`
      }}>
        <p className="text-sm" style={{
          color: `var(--color-text, #e5e5e5)`
        }}>
          <strong>Important:</strong> Your logo must be hosted on a reliable, permanent URL. Consider
          using IPFS or a similar decentralized storage solution for maximum reliability.
        </p>
      </div>

      <h2>Step 4: Submit and Wait ⏳</h2>
      <p>
        Almost there! After submitting your information:
      </p>
      <ul>
        <li>The block explorer team will review your submission (usually takes 3-7 days)</li>
        <li>Keep an eye on your email for any update requests</li>
        <li>Once approved, your logo will automatically appear on Etherscan/BSCScan</li>
        <li>Other platforms (Trust Wallet, MetaMask, etc.) will gradually pick up the update</li>
      </ul>

      <h2>Troubleshooting Tips 🛠️</h2>
      <ul>
        <li>If your submission is rejected, carefully read the feedback and try again</li>
        <li>Make sure your logo meets all size and format requirements</li>
        <li>Verify that you're using a permanent, accessible URL for your logo</li>
        <li>Double-check that you're the token owner or have appropriate permissions</li>
      </ul>

      <div className="not-prose mt-8">
        <Card className="border-2" style={{
          borderColor: `var(--color-header-accent, #0A74FF)`
        }}>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <ImageIcon className="w-6 h-6" style={{
                color: `var(--color-header-accent, #0A74FF)`
              }} />
              Ready to Create Your Own Token?
            </h3>
            <p className="mb-4">
              Start your crypto journey with our easy-to-use token creator. Create a professional token on Ethereum or BSC
              in minutes, then follow this guide to add your logo!
            </p>
            <Button
              className="text-white hover:opacity-90"
              style={{
                background: `linear-gradient(to right, var(--color-background, #000000), var(--color-header-accent, #0A74FF), var(--color-background, #000000))`
              }}
              onClick={() => window.open('https://mdbapp.io/bscchain/create', '_blank')}
            >
              Create Your Token
              <Rocket className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </article>
  );
}
